import * as React from "react"
import { cn } from "../../components/lib/utils.js"

const Card = React.forwardRef(({
  className,
  variant = 'default',
  hover = false,
  ...props
}, ref) => {
  const variants = {
    default: "bg-background-light border-border text-foreground",
    parchment: "bg-background-paper text-foreground-dark border-secondary/30 bg-paper-texture bg-cover",
    primary: "bg-primary-900/90 border-primary-700 text-foreground",
    secondary: "bg-secondary-900/90 border-secondary-700 text-foreground",
    accent: "bg-accent-900/90 border-accent-700 text-foreground",
    outlined: "bg-transparent border-border text-foreground"
  }

  const hoverEffect = hover ? "transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" : "";

  return (
    <div
      ref={ref}
      className={cn(
        "rounded-lg border shadow-soft overflow-hidden",
        variants[variant],
        hoverEffect,
        className
      )}
      {...props}
    />
  )
})
Card.displayName = "Card"

const CardHeader = React.forwardRef(({ className, variant = 'default', ...props }, ref) => {
  const variants = {
    default: "border-b border-border/50",
    parchment: "border-b-2 border-secondary-900/20",
    fancy: "border-b border-primary/30 bg-primary-900/10",
    golden: "border-b-2 border-secondary/30 bg-secondary-900/10"
  }

  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col space-y-1.5 p-6",
        variants[variant],
        className
      )}
      {...props}
    />
  )
})
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef(({ className, variant = 'default', ...props }, ref) => {
  const variants = {
    default: "text-foreground",
    primary: "text-primary",
    secondary: "text-secondary",
    accent: "text-accent",
    golden: "text-secondary font-display"
  }

  return (
    <h3
      ref={ref}
      className={cn(
        "text-xl font-semibold leading-none tracking-tight",
        variants[variant],
        className
      )}
      {...props}
    />
  )
})
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-foreground-muted mt-2", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-4", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef(({ className, separated = false, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center p-6 pt-0",
      separated && "border-t border-border/50 mt-4 pt-4",
      className
    )}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }